import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Trophy, Table, CalendarDays, ListChecks, Users, ScrollText, ArrowLeft, Landmark, ArrowUpRight, Info, Globe2 } from "lucide-react";
import { api } from "../lib/api";
import { Flag } from "../components/Flag";
import { Loader, Empty, Card } from "../components/UI";
import { MatchStatusBadge } from "../components/Arena";
import { LeagueTable } from "../components/LeagueTable";
import { ClubCrest } from "../components/ClubCrest";
import { metaOf, CompStatus } from "./CompetitionsList";

const CONTAINER = "mx-auto max-w-[1600px] px-4 sm:px-8 lg:px-12";

// Verified dedicated hubs (Phase 6 bespoke pages). Only real, confirmed mappings.
const HUB = {
  "CUP-GRE-ARENABOOK-2027": { to: "/cup", label: "Open Cup Bracket Hub" },
  "WSTF-ECC-2027": { to: "/continental/1", label: "Open Continental Hub" },
  "WSTF-EC-2027": { to: "/continental/2", label: "Open Continental Hub" },
  "WSTF-ECL-2027": { to: "/continental/3", label: "Open Continental Hub" },
};

const ALL_TABS = {
  standings: { label: "Standings", Icon: Table },
  fixtures: { label: "Fixtures", Icon: CalendarDays },
  results: { label: "Results", Icon: ListChecks },
  teams: { label: "Teams", Icon: Users },
  rules: { label: "Rules", Icon: ScrollText },
};

export default function CompetitionPage() {
  const { compId } = useParams();
  const [comp, setComp] = useState(null);
  const [fed, setFed] = useState(null);
  const [standings, setStandings] = useState(null);
  const [fixtures, setFixtures] = useState(null);
  const [results, setResults] = useState(null);
  const [rules, setRules] = useState(null);
  const [tab, setTab] = useState(null);

  useEffect(() => {
    setComp(null); setTab(null); setFed(null);
    api.get(`/competitions/${compId}`).then(({ data }) => {
      setComp(data);
      if (data.federation_id) api.get(`/federations/${data.federation_id}`).then(({ data: f }) => setFed(f)).catch(() => {});
    }).catch(() => setComp(false));
    api.get(`/competitions/${compId}/fixtures`).then(({ data }) => setFixtures(data)).catch(() => setFixtures({ fixtures: [], total: 0, matchweeks: [] }));
    api.get(`/competitions/${compId}/results`).then(({ data }) => setResults(data)).catch(() => setResults({ results: [], total: 0 }));
    api.get(`/competitions/${compId}/rules`).then(({ data }) => setRules(data)).catch(() => setRules(null));
    api.get(`/competitions/${compId}/standings`).then(({ data }) => setStandings(data)).catch(() => setStandings({ standings: [], unsupported: true }));
  }, [compId]);

  if (comp === false) return <div className={`${CONTAINER} py-16 animate-fade-up`}><Empty label="Competition not found" /></div>;
  if (!comp) return <Loader label="Loading competition…" />;

  const m = metaOf(comp.competition_type);
  const isLeague = comp.competition_type === "DOMESTIC_LEAGUE" || comp.competition_type == null;
  const hub = HUB[comp.id];

  // Format-aware tab set — never force a league table onto a non-league competition.
  const tabs = [];
  if (isLeague) {
    tabs.push("standings", "fixtures", "results", "teams", "rules");
  } else {
    if ((comp.clubs || []).length > 0) tabs.push("teams");
    if (fixtures?.total > 0) tabs.push("fixtures");
    if (results?.total > 0) tabs.push("results");
    tabs.push("rules");
  }
  const activeTab = tab && tabs.includes(tab) ? tab : tabs[0];
  const Icon = m.Icon;

  return (
    <div className="animate-fade-up pb-16">
      {/* Full-width cinematic competition header */}
      <section className="relative overflow-hidden border-b border-[rgba(212,175,55,0.22)] bg-gradient-to-b from-[#141007] to-[#000000]">
        <div className="absolute inset-0 mesh-grid opacity-[0.16] pointer-events-none" />
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(100% 140% at 85% 0%, rgba(212,175,55,0.14) 0%, transparent 55%)" }} />
        <div className={`${CONTAINER} relative py-10 sm:py-12`}>
          <Link to="/competitions" data-testid="competition-back" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-[#E5B842] mb-5" style={{ transition: "color 0.15s ease" }}>
            <ArrowLeft className="w-4 h-4" /> All competitions
          </Link>
          <div className="flex flex-col lg:flex-row lg:items-center gap-5">
            <div className="shrink-0 w-16 h-16 rounded-2xl bg-[#000000] border border-[#E5B842]/40 flex items-center justify-center">
              <Icon className="w-8 h-8 text-[#E5B842]" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2 text-[11px] font-mono uppercase tracking-[0.2em] text-slate-400">
                <span className="text-[#E5B842] font-bold">{m.label}</span>
                <span className="text-slate-600">·</span><span>{comp.discipline}</span>
                {comp.season && <><span className="text-slate-600">·</span><span>Season {comp.season.name}</span></>}
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight text-slate-50 leading-none mt-1.5 wsbf-slant-block break-words">{comp.name}</h1>
              <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
                <CompStatus status={comp.status} />
                <span data-testid="competition-territory" className="font-mono text-slate-300 inline-flex items-center gap-1.5">
                  {comp.country ? <Flag country={comp.country} width={20} /> : <Globe2 className="w-4 h-4 text-slate-400 shrink-0" />}
                  {comp.country ? comp.country.name : "International"}
                </span>
                {typeof comp.team_count === "number" && comp.team_count > 0 && <span className="font-mono text-slate-400">{comp.team_count} teams</span>}
                {fed && (
                  <Link to={`/federation/${fed.wsbf_fed_id}`} data-testid="competition-federation-link" className="inline-flex items-center gap-1.5 font-mono text-slate-300 hover:text-[#E5B842]" style={{ transition: "color 0.15s ease" }}>
                    <Landmark className="w-3.5 h-3.5 text-[#E5B842]" />{fed.name}
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className={`${CONTAINER} pt-6`}>
        {/* Dedicated-hub banner for format-rich bespoke competitions (cup bracket / continental tiers) */}
        {hub && (
          <Link to={hub.to} data-testid="competition-hub-link" className="group flex items-center justify-between gap-4 rounded-xl foil-border bg-gradient-to-r from-[#1a1407] to-[#070707] p-4 sm:p-5 mb-6 hover:border-[#E5B842]/60" style={{ transition: "border-color 0.15s ease" }}>
            <div className="flex items-center gap-3">
              <Trophy className="w-6 h-6 text-[#E5B842] shrink-0" />
              <div>
                <div className="font-heading font-bold uppercase tracking-tight text-slate-50">{hub.label}</div>
                <div className="text-xs text-slate-400">This competition is a knockout/continental format — open the full bracket & stage hub.</div>
              </div>
            </div>
            <ArrowUpRight className="w-5 h-5 text-slate-500 group-hover:text-[#E5B842] shrink-0" style={{ transition: "color 0.15s ease" }} />
          </Link>
        )}

        {/* Format note for non-league competitions without a standings table */}
        {!isLeague && (
          <div className="flex items-start gap-2 text-xs text-slate-500 font-mono mb-5" data-testid="competition-format-note">
            <Info className="w-4 h-4 text-slate-600 shrink-0 mt-0.5" />
            This is a {m.label.toLowerCase()} competition — it does not use a domestic league table.
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 border-b border-[#2b2512] mb-6 overflow-x-auto no-scrollbar">
          {tabs.map((key) => {
            const T = ALL_TABS[key];
            return (
              <button key={key} data-testid={`tab-${key}`} onClick={() => setTab(key)}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold uppercase tracking-wide border-b-2 whitespace-nowrap ${activeTab === key ? "border-[#E5B842] text-[#E5B842]" : "border-transparent text-slate-400 hover:text-slate-200"}`}
                style={{ transition: "color 0.15s ease" }}>
                <T.Icon className="w-4 h-4" /> {T.label}
              </button>
            );
          })}
        </div>

        {activeTab === "standings" && (!standings ? <Loader /> : (
          standings.unsupported ? <Empty label="No league standings for this competition" />
            : <LeagueTable standings={standings.standings} template={standings.template} />
        ))}

        {activeTab === "fixtures" && (!fixtures ? <Loader /> : (
          fixtures.fixtures.length === 0 ? <Empty label="No fixtures published yet" /> : <FixtureList data={fixtures} />
        ))}

        {activeTab === "results" && (!results ? <Loader /> : (
          results.results.length === 0 ? <Empty label="No results yet" /> : (
            <div className="space-y-2" data-testid="results-list">
              {results.results.map((mt) => <ResultRow key={mt.id} m={mt} />)}
            </div>
          )
        ))}

        {activeTab === "teams" && (
          (comp.clubs || []).length === 0 ? <Empty label="No participating teams listed" /> : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="teams-grid">
              {comp.clubs.map((c) => (
                <Link key={c.id} to={`/club/${c.id}`} data-testid={`comp-team-${c.wsbf_club_id || c.id}`} className="flex items-center gap-3 rounded-xl bg-[#0f0f0f] border border-[#2b2512] p-3 hover:border-[#E5B842]/40" style={{ transition: "border-color 0.15s ease" }}>
                  <ClubCrest club={c} size={36} />
                  <span className="font-heading font-bold uppercase tracking-tight text-slate-100 leading-snug">{c.name}</span>
                </Link>
              ))}
            </div>
          )
        )}

        {activeTab === "rules" && (!rules ? <Loader /> : <RulesPanel rules={rules} />)}
      </div>
    </div>
  );
}

function FixtureList({ data }) {
  const [mw, setMw] = useState(data.matchweeks?.[0] || 1);
  const list = data.fixtures.filter((f) => f.matchweek === mw);
  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-4" data-testid="matchweek-selector">
        {data.matchweeks.map((w) => (
          <button key={w} onClick={() => setMw(w)}
            className={`px-2.5 py-1 rounded text-xs font-mono font-bold ${w === mw ? "bg-[#E5B842] text-[#000000]" : "bg-[#161616] text-slate-400 border border-[#2b2512]"}`}>
            MW{w}
          </button>
        ))}
      </div>
      <div className="space-y-2">
        {list.map((mt) => (
          <Link key={mt.id} to={`/match/${mt.id}`} className="flex items-center justify-between gap-3 rounded-lg bg-[#0f0f0f] border border-[#2b2512] p-3 hover:border-[#E5B842]/40" style={{ transition: "border-color 0.15s ease" }}>
            <span className="flex-1 text-right font-semibold text-slate-100 truncate">{mt.home_club?.name}</span>
            <span className="font-heading font-black text-[#E5B842] tabular-nums w-20 text-center">
              {mt.status === "FINAL" ? `${mt.match_result?.home_team_points}–${mt.match_result?.away_team_points}` : "vs"}
            </span>
            <span className="flex-1 font-semibold text-slate-100 truncate">{mt.away_club?.name}</span>
            <MatchStatusBadge status={mt.status} />
          </Link>
        ))}
      </div>
    </div>
  );
}

function ResultRow({ m }) {
  const mr = m.match_result;
  return (
    <Link to={`/match/${m.id}`} data-testid={`result-${m.public_match_id}`} className="flex items-center justify-between gap-3 rounded-lg bg-[#0f0f0f] border border-[#2b2512] p-3 hover:border-[#E5B842]/40" style={{ transition: "border-color 0.15s ease" }}>
      <span className="text-[10px] font-mono text-slate-500 w-10">MW{m.matchweek}</span>
      <span className="flex-1 text-right font-semibold text-slate-100 truncate">{m.home_club?.name}</span>
      <span className="font-heading font-black text-[#E5B842] tabular-nums w-16 text-center">{mr?.home_team_points}–{mr?.away_team_points}</span>
      <span className="flex-1 font-semibold text-slate-100 truncate">{m.away_club?.name}</span>
      <span className="text-[10px] font-mono text-slate-500 w-20 text-right hidden sm:block">D {mr?.home_duels_won}-{mr?.away_duels_won}</span>
    </Link>
  );
}

function RulesPanel({ rules }) {
  const r = rules.team_point_rules;
  const cfg = rules.competition_config || {};
  return (
    <div className="space-y-6" data-testid="rules-panel">
      <Card className="p-6">
        <h3 className="font-heading text-xl font-bold uppercase text-slate-50 mb-1">Team Point Rules <span className="text-xs font-mono text-[#E5B842]">({r.rules_version})</span></h3>
        <p className="text-slate-400 text-sm mb-4">Winning coupon total odds → team points.</p>
        <div className="grid sm:grid-cols-3 gap-3">
          {r.tiers.map((t, i) => (
            <div key={i} className="rounded-lg bg-[#0f0f0f] border border-[#2b2512] p-4 text-center">
              <div className="font-heading text-3xl font-black text-[#E5B842]">+{t.points}</div>
              <div className="font-mono text-xs text-slate-400 mt-1">{t.min}{t.max ? `–${t.max}` : "+"}</div>
            </div>
          ))}
        </div>
      </Card>
      <Card className="p-6">
        <h3 className="font-heading text-xl font-bold uppercase text-slate-50 mb-3">League Points</h3>
        <div className="flex gap-6 font-mono text-sm text-slate-300">
          <span>WIN = <b className="text-[#E5B842]">{rules.league_points.win}</b></span>
          <span>DRAW = <b className="text-[#E5B842]">{rules.league_points.draw}</b></span>
          <span>LOSS = <b className="text-[#E5B842]">{rules.league_points.loss}</b></span>
        </div>
      </Card>
      <Card className="p-6">
        <h3 className="font-heading text-xl font-bold uppercase text-slate-50 mb-3">WSTF Tie-Break Standard</h3>
        <ol className="list-decimal list-inside space-y-1 text-sm text-slate-300 font-mono">
          {rules.tie_break_order.map((s, i) => <li key={i}>{s}</li>)}
        </ol>
      </Card>
      {cfg.regulation_duels && (
        <Card className="p-6">
          <h3 className="font-heading text-xl font-bold uppercase text-slate-50 mb-3">Match Regulation</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            {[["Duels", cfg.regulation_duels], ["Coupons / team", cfg.coupons_per_team], ["Max / player", cfg.max_coupons_per_player], ["Selections", `${cfg.min_selections}–${cfg.max_selections}`], ["Horizon (h)", cfg.event_horizon_hours]].map(([l, v]) => (
              <div key={l}><div className="text-[10px] uppercase text-slate-500 font-mono">{l}</div><div className="font-heading text-xl font-black text-slate-50">{v}</div></div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
